import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';

/**
 * Public Landing Page — Kivo
 * "Guided Childcare" positioning. WhatsApp-first proof.
 */
export const PublicLanding = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF8]">

      {/* ═══════════════════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-16 md:pt-32 md:pb-24 text-center">

        {/* Brand mark */}
        <div className="inline-flex items-center gap-2.5 mb-10">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Heart className="w-5 h-5 text-white fill-white" />
          </div>
          <span className="text-xl font-bold text-slate-800 font-heading tracking-tight">Kivo</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight font-heading mb-5">
          Every school day,<br className="hidden md:block" /> handled.
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-slate-500 max-w-xl mx-auto mb-10 leading-relaxed">
          Kivo guides your caregiver through the day on WhatsApp,
          develops your child with age-appropriate activities,
          and sends you a calm evening summary.
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="landing-ctas">
          <button
            onClick={() => navigate('/signup')}
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-800 transition-colors shadow-sm"
            data-testid="parent-login-cta"
          >
            Start 7-day free trial
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('a-day-with-kivo');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-slate-700 border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition-colors"
            data-testid="how-it-works-cta"
          >
            See a full day
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          A DAY WITH KIVO — 4 WhatsApp moments
          ═══════════════════════════════════════════════════════════════════ */}
      <section id="a-day-with-kivo" className="bg-white border-y border-slate-100">
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-24">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-3 font-heading">
            A day with Kivo
          </h2>
          <p className="text-sm text-slate-500 text-center mb-14 max-w-md mx-auto">
            Four WhatsApp messages. That's it.
          </p>

          {/* Timeline spine */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[23px] top-0 bottom-0 w-px bg-slate-200 hidden md:block" />

            <div className="space-y-10">

              {/* 7:30 AM — Morning plan */}
              <WhatsAppMoment
                time="7:30 AM"
                label="Morning plan sent to your caregiver"
                direction="outgoing"
              >
                <p className="text-sm leading-relaxed">
                  Good morning Kajal ji! ☀️<br /><br />
                  Arya ka aaj ka plan:<br />
                  📋 Bag mein: Math notebook + water bottle + snack box<br />
                  📝 School task: Permission slip submit karna hai<br />
                  🎨 Activity: Colour mixing — 2 paint colours mix karke naya colour banao<br /><br />
                  Sab pack ho jaye toh 👍 bhej do!
                </p>
              </WhatsAppMoment>

              {/* 3:15 PM — Caregiver reply */}
              <WhatsAppMoment
                time="3:15 PM"
                label="Your caregiver replies"
                direction="incoming"
              >
                <p className="text-sm leading-relaxed">
                  Arya ghar aa gayi 🏠<br />
                  Mood: khush hai, bohot khelke aayi<br />
                  Lunch: poora kha liya 🍱<br />
                  Permission slip de diya teacher ko ✅
                </p>
              </WhatsAppMoment>

              {/* 4:30 PM — Activity done */}
              <WhatsAppMoment
                time="4:30 PM"
                label="Activity completed"
                direction="incoming"
              >
                <p className="text-sm leading-relaxed">
                  Activity done! 🎨<br />
                  Arya ne red aur yellow mix kiya — orange bana.<br />
                  Bohot enjoy kiya, 20 min laga.
                </p>
              </WhatsAppMoment>

              {/* 9:00 PM — Evening summary to parent */}
              <WhatsAppMoment
                time="9:00 PM"
                label="Your evening summary"
                direction="outgoing"
                isParent
              >
                <p className="text-sm leading-relaxed">
                  Arya's day — Monday, 10 March<br /><br />
                  Home at 3:15. Kajal said she came back happy, ate her full lunch. Permission slip was handed to the teacher.<br /><br />
                  This afternoon, Kajal guided her through colour mixing — red and yellow into orange. She stayed with it for 20 minutes, which is strong focus for her age.<br /><br />
                  <span className="text-slate-400">
                    She's been responding well to hands-on creative work. You might enjoy doing something similar together this weekend.
                  </span>
                </p>
              </WhatsAppMoment>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-12">
            You didn't call anyone. You didn't check email. You just knew.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          WHAT CHANGES FOR YOUR CAREGIVER
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="max-w-2xl mx-auto px-6 py-20 md:py-24">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-3 font-heading">
          What changes for your caregiver
        </h2>
        <p className="text-sm text-slate-500 text-center mb-10 max-w-md mx-auto">
          No app needed. Everything happens on WhatsApp, in her language.
        </p>

        <div className="space-y-3 max-w-lg mx-auto">
          {[
            {
              text: 'Daily plan every morning',
              detail: 'What to pack, what task is due, what activity to guide — in Hinglish, on WhatsApp.',
            },
            {
              text: 'Natural reporting',
              detail: 'A quick message or a thumbs up. Kivo understands pickup, meals, mood, and completion.',
            },
            {
              text: 'Invisible skill upgrade',
              detail: 'Age-appropriate activities and structured routines, delivered daily. She gets better without trying.',
            },
          ].map(({ text, detail }) => (
            <div key={text} className="bg-white rounded-2xl border border-slate-100 px-5 py-4">
              <p className="text-sm font-semibold text-slate-900 mb-1">{text}</p>
              <p className="text-sm text-slate-500 leading-relaxed">{detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          WHAT CHANGES FOR YOUR CHILD
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-24">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-3 font-heading">
            What changes for your child
          </h2>
          <p className="text-sm text-slate-500 text-center mb-12 max-w-md mx-auto">
            Screen time replaced with guided play. Every weekday.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
            {[
              { area: 'Creative', example: 'Colour mixing, paper collage, pattern drawing' },
              { area: 'Motor Skills', example: 'Obstacle course, balancing games, clay shaping' },
              { area: 'Cognitive', example: 'Sorting by colour, simple puzzles, memory games' },
              { area: 'Language', example: 'Story retelling, rhyme games, picture description' },
              { area: 'Social', example: 'Turn-taking games, sharing exercises, role play' },
            ].map(({ area, example }) => (
              <div key={area} className="rounded-xl bg-[#FAFAF8] border border-slate-100 px-5 py-4">
                <p className="text-sm font-semibold text-slate-800 mb-1">{area}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{example}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-400 mt-8">
            Selected daily by age, energy, and recent activity — rotated across all five areas.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          PRICING
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="max-w-lg mx-auto px-6 py-20 md:py-24 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 font-heading">
          One plan. One price.
        </h2>
        <p className="text-sm text-slate-500 mb-10">
          Everything your family needs. Cancel anytime.
        </p>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-baseline justify-center gap-1 mb-1">
            <span className="text-4xl font-bold text-slate-900 font-heading">Rs. 1,499</span>
            <span className="text-sm text-slate-500">/month</span>
          </div>
          <p className="text-sm text-slate-400 mb-8">per family · 7-day free trial</p>

          <button
            onClick={() => navigate('/signup')}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-800 transition-colors"
            data-testid="pricing-cta"
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </button>

          <p className="text-xs text-slate-400 mt-5">
            No credit card required. Set up takes 5 minutes.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-slate-100 py-8 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-primary flex items-center justify-center">
              <Heart className="w-3 h-3 text-white fill-white" />
            </div>
            <p className="text-sm text-slate-400">© 2026 Kivo. Guided childcare for Indian families.</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Privacy</a>
            <a href="/terms" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Terms</a>
            <a
              href="https://wa.me/919217630049"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              WhatsApp us
            </a>
            <a href="/invite" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Invite</a>
            <button
              onClick={() => navigate('/operator/login')}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Operator
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

/**
 * WhatsApp message bubble component for the day flow section.
 */
function WhatsAppMoment({ time, label, direction, isParent, children }) {
  const isOutgoing = direction === 'outgoing';

  return (
    <div className="md:pl-14 relative">
      {/* Timeline dot */}
      <div className="absolute left-[17px] top-1 w-2.5 h-2.5 rounded-full bg-slate-300 hidden md:block" />

      {/* Time + label */}
      <div className="flex items-baseline gap-3 mb-2.5">
        <span className="text-xs font-medium text-slate-600 tabular-nums whitespace-nowrap">{time}</span>
        <span className="text-xs text-slate-400">{label}</span>
      </div>

      {/* Message bubble — intentionally low-chrome */}
      <div
        className={`rounded-xl px-4 py-3.5 max-w-sm ${
          isOutgoing
            ? isParent
              ? 'bg-[#E8F4E8] ml-auto'
              : 'bg-[#DCF8C6]'
            : 'bg-white border border-slate-150 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default PublicLanding;
